import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { withdrawalsAPI, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ArrowLeft, Bank, CheckCircle, Clock, XCircle, Funnel } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function AdminWithdrawalsPage() {
  const { user } = useAuth();
  const [withdrawals, setWithdrawals] = useState([]);
  const [filter, setFilter] = useState('');
  const [actionDialog, setActionDialog] = useState(null);
  const [adminNote, setAdminNote] = useState('');
  const [processing, setProcessing] = useState(false);

  const loadWithdrawals = useCallback(async () => {
    try {
      const { data } = await withdrawalsAPI.getAll(filter || undefined);
      setWithdrawals(Array.isArray(data) ? data : (data?.items || []));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [filter]);

  useEffect(() => { loadWithdrawals(); }, [loadWithdrawals]);

  const handleAction = async (action) => {
    if (!actionDialog) return;
    setProcessing(true);
    try {
      if (action === 'approve') {
        await withdrawalsAPI.approve(actionDialog.withdrawal_id, adminNote);
        toast.success('Withdrawal approved');
      } else {
        await withdrawalsAPI.reject(actionDialog.withdrawal_id, adminNote);
        toast.success('Withdrawal rejected — funds returned to user');
      }
      setActionDialog(null);
      setAdminNote('');
      loadWithdrawals();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setProcessing(false);
    }
  };

  const statusBadge = (status) => {
    const map = {
      pending: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', icon: <Clock className="w-3 h-3" weight="fill" />, label: 'Pending' },
      approved: { bg: 'bg-green-500/10', text: 'text-green-400', icon: <CheckCircle className="w-3 h-3" weight="fill" />, label: 'Approved' },
      rejected: { bg: 'bg-red-500/10', text: 'text-red-400', icon: <XCircle className="w-3 h-3" weight="fill" />, label: 'Rejected' },
    };
    const s = map[status] || map.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${s.bg} ${s.text}`}>
        {s.icon} {s.label}
      </span>
    );
  };

  const methodLabel = (m) => {
    if (m === 'bank_transfer') return 'Bank Transfer';
    if (m === 'paypal') return 'PayPal';
    if (m === 'cashapp') return 'Cash App';
    return m;
  };

  const pendingCount = withdrawals.filter(w => w.status === 'pending').length;

  if (user?.role !== 'admin') {
    return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-white">Admin access required</div>;
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <header className="sticky top-0 z-20 bg-[#0A0A0A]/90 border-b border-[#222222] backdrop-blur-md">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2 text-sm text-[#999999] hover:text-[#22C55E] transition" data-testid="back-to-admin">
            <ArrowLeft className="w-4 h-4" /> Admin
          </Link>
          <div className="flex items-center gap-2">
            <Bank className="w-5 h-5 text-[#22C55E]" />
            <span className="text-white font-bold">Withdrawals</span>
            {pendingCount > 0 && (
              <span className="bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded-full text-xs font-bold">{pendingCount} pending</span>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {/* Filter */}
        <div className="flex gap-2" data-testid="filter-bar">
          {['', 'pending', 'approved', 'rejected'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f || 'all'}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                filter === f
                  ? 'bg-[#22C55E] text-black'
                  : 'bg-[#111111] text-[#999999] border border-[#333333] hover:border-[#555555]'
              }`}
            >
              {f ? f.charAt(0).toUpperCase() + f.slice(1) : 'All'}
            </button>
          ))}
        </div>

        {withdrawals.length === 0 ? (
          <div className="text-center py-16 text-[#999999]">
            <Bank className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No withdrawals found</p>
          </div>
        ) : (
          withdrawals.map(w => (
            <Card key={w.withdrawal_id} className="bg-[#111111] border-[#222222]" data-testid={`admin-wd-${w.withdrawal_id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <p className="text-white font-bold text-lg">${w.amount.toFixed(2)}</p>
                      {statusBadge(w.status)}
                    </div>
                    <p className="text-sm text-[#999999]">
                      <span className="text-white">{w.user_name || 'User'}</span> &middot; {w.user_email}
                    </p>
                    <p className="text-xs text-[#999999]">
                      {methodLabel(w.method)} &middot; {new Date(w.created_at).toLocaleString()}
                    </p>

                    {/* Bank details */}
                    {w.method === 'bank_transfer' && (
                      <div className="bg-[#0A0A0A] rounded-lg p-2 mt-2 text-xs text-[#999999] space-y-0.5">
                        <p>Bank: <span className="text-white">{w.bank_name}</span></p>
                        <p>Holder: <span className="text-white">{w.account_holder}</span></p>
                        <p>Routing: <span className="text-white">{w.routing_number}</span></p>
                        <p>Account: <span className="text-white">****{w.account_last4}</span></p>
                      </div>
                    )}
                    {w.method === 'paypal' && (
                      <div className="bg-[#0A0A0A] rounded-lg p-2 mt-2 text-xs text-[#999999]">
                        PayPal: <span className="text-white">{w.paypal_email}</span>
                      </div>
                    )}
                    {w.method === 'cashapp' && (
                      <div className="bg-[#0A0A0A] rounded-lg p-2 mt-2 text-xs text-[#999999]">
                        Cash App: <span className="text-white">{w.cashapp_tag}</span>
                      </div>
                    )}

                    {w.notes && <p className="text-xs text-[#999999] mt-1">User note: {w.notes}</p>}
                    {w.admin_note && <p className="text-xs text-yellow-400/70 mt-1">Admin: {w.admin_note}</p>}
                  </div>

                  {w.status === 'pending' && (
                    <div className="flex flex-col gap-1.5 shrink-0">
                      <Button
                        size="sm"
                        onClick={() => { setActionDialog({ ...w, action: 'approve' }); setAdminNote(''); }}
                        className="bg-[#22C55E] hover:bg-[#1EA34B] text-black text-xs font-bold"
                        data-testid={`approve-${w.withdrawal_id}`}
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setActionDialog({ ...w, action: 'reject' }); setAdminNote(''); }}
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10 text-xs"
                        data-testid={`reject-${w.withdrawal_id}`}
                      >
                        Reject
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </main>

      {/* Action Dialog */}
      <Dialog open={!!actionDialog} onOpenChange={() => setActionDialog(null)}>
        <DialogContent className="bg-[#1A1A1A] border-[#333333] text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">
              {actionDialog?.action === 'approve' ? 'Approve' : 'Reject'} Withdrawal — ${actionDialog?.amount?.toFixed(2)}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-[#999999]">
              {actionDialog?.action === 'approve'
                ? `Confirm you have sent $${actionDialog?.amount?.toFixed(2)} via ${methodLabel(actionDialog?.method)} to ${actionDialog?.user_name}.`
                : `This will return $${actionDialog?.amount?.toFixed(2)} to the user's wallet.`}
            </p>
            <div>
              <Label className="text-xs text-[#999999]">Admin Note (Optional)</Label>
              <Textarea
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                placeholder={actionDialog?.action === 'approve' ? 'Transaction reference, etc...' : 'Reason for rejection...'}
                className="bg-[#111111] border-[#333333] text-white mt-1 min-h-[60px]"
                data-testid="admin-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog(null)} className="border-[#333333] text-white">Cancel</Button>
            {actionDialog?.action === 'approve' ? (
              <Button onClick={() => handleAction('approve')} disabled={processing} className="bg-[#22C55E] hover:bg-[#1EA34B] text-black font-bold" data-testid="confirm-approve">
                {processing ? 'Processing...' : 'Confirm Approve'}
              </Button>
            ) : (
              <Button onClick={() => handleAction('reject')} disabled={processing} className="bg-red-600 hover:bg-red-700 text-white font-bold" data-testid="confirm-reject">
                {processing ? 'Processing...' : 'Confirm Reject'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
